import type { InboxItemType } from "@remi/constants";
import { isOneOf } from "@remi/ts-utils";

import { type FeedTab, PathToFeedTab } from "~/hooks/feed/useFeedNavigation";

interface ResolvedInboxItem {
  type: InboxItemType;
  id: string;
  conversationId?: string;
  tab?: FeedTab;
}

const INBOX_ITEM_SEGMENTS = [
  "ticket",
  "conversation",
  "task",
  "escalation",
] as const satisfies readonly InboxItemType[];

const firstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

// The segment after a task id is a feed-tab path (`task/{id}/{tabPath}/{conversationId}`) — what onFeedNavigate
// writes and the standalone task page reads; `conversation/{id}` is the legacy shape inboxItemHref still emits.
const resolveTaskFeed = (sub?: string, subId?: string): { tab?: FeedTab; conversationId?: string } => {
  if (sub === "conversation") {
    return { conversationId: subId };
  }
  const tab = sub && isOneOf(sub, Object.keys(PathToFeedTab)) ? PathToFeedTab[sub] : undefined;
  return { tab, conversationId: tab && subId ? subId : undefined };
};

// Legacy `?ticketId=` etc. fallback is load-bearing: `has`-based next.config redirects
// don't fire on client-side soft navigations, so an un-migrated in-app link would otherwise
// render a blank detail pane.
export const resolveInboxItem = (query: Record<string, string | string[] | undefined>): ResolvedInboxItem | null => {
  const item = Array.isArray(query.item) ? query.item : [];
  const [segment, id, sub, subId] = item;
  if (id && isOneOf(segment, INBOX_ITEM_SEGMENTS)) {
    if (segment === "task") {
      const { tab, conversationId } = resolveTaskFeed(sub, subId);
      return { type: segment, id, tab, conversationId };
    }
    return { type: segment, id };
  }

  const ticketId = firstValue(query.ticketId);
  const escalationId = firstValue(query.escalationId);
  const taskId = firstValue(query.taskId);
  const conversationId = firstValue(query.conversationId);
  if (ticketId) {
    return { type: "ticket", id: ticketId };
  }
  if (escalationId) {
    return { type: "escalation", id: escalationId };
  }
  if (taskId) {
    return { type: "task", id: taskId, conversationId: conversationId ?? undefined };
  }
  if (conversationId) {
    return { type: "conversation", id: conversationId };
  }
  return null;
};
